import React, { createContext, useContext, useEffect, useState } from 'react'
import { Button, stylesButton, type ButtonAlign } from '../Button/Button'
import FocusLock from 'react-focus-lock'
import Portal from '../Portal/Portal'
import { MemoryRouter, Route, Switch, useHistory } from 'react-router-dom'
import styles from './ActionsModal.module.scss'
import clsx from 'clsx'

type ActionsProps = {
  children: React.ReactNode
  initialPage: string
  actionInProgress: boolean
  showModal?: boolean
  trigger?: (props: { toggleModal: () => void; showModalInt: boolean }) => React.ReactNode
  onClose?: (toggle: boolean) => void
  overlayClassName?: string
  modalClassName?: string
  allowOverflow?: boolean
}

type ModalContextType = {
  showModal: boolean
  toggleModal: (show: boolean) => void
  isActionInProgress: boolean
  setIsActionInProgress: (actionInProgress: boolean) => void
  isPendingPage: boolean
  setIsPendingPage: (pendingState: boolean) => void
  allowOverflow?: boolean
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

const useModalContext = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider')
  }
  return context
}

const Modal = ({
  children,
  initialPage,
  actionInProgress,
  showModal,
  trigger,
  onClose,
  overlayClassName,
  modalClassName,
  allowOverflow,
}: ActionsProps) => {
  const [showModalInt, setShowModal] = useState(false)
  const [isActionInProgress, setIsActionInProgress] = useState(false)
  const [isPendingPage, setIsPendingPage] = useState(false)

  const toggleModal = (toggle?: boolean) => {
    if (isActionInProgress) return
    if (toggle !== undefined) {
      setShowModal(toggle)
      onClose?.(toggle)
    } else {
      setShowModal(!showModalInt)
      onClose?.(!showModalInt)
    }
  }
  useEffect(() => {
    if (showModal === undefined) return
    setShowModal(showModal)
  }, [showModal])

  useEffect(() => {
    setIsActionInProgress(actionInProgress)
  }, [actionInProgress])

  return (
    <ModalContext.Provider
      value={{
        showModal: showModalInt,
        toggleModal,
        isActionInProgress,
        setIsActionInProgress,
        isPendingPage,
        setIsPendingPage,
        allowOverflow,
      }}
    >
      {trigger && trigger({ toggleModal, showModalInt })}
      {showModalInt && (
        <Portal>
          <FocusLock autoFocus={true} data-no-focus-lock>
            <div className={clsx(styles.actionsOverlay, overlayClassName)}>
              <div
                className={clsx(
                  styles.actionsModal,
                  allowOverflow && styles.allowOverflow,
                  modalClassName
                )}
              >
                <MemoryRouter initialEntries={[initialPage]}>
                  <Switch>{children}</Switch>
                </MemoryRouter>
              </div>
            </div>
          </FocusLock>
        </Portal>
      )}
    </ModalContext.Provider>
  )
}

const Page = ({
  header,
  path,
  children,
  closeAfter,
  invertCloseButton,
  className,
}: {
  header: string
  path: string
  children: React.ReactNode
  closeAfter?: number
  invertCloseButton?: boolean
  className?: string
}) => {
  const context = useModalContext()

  useEffect(() => {
    if (closeAfter) {
      const timer = setTimeout(() => {
        context.toggleModal(false)
      }, closeAfter)

      return () => clearTimeout(timer)
    }
  }, [closeAfter, context])

  // Separate children into body and footer content
  const childrenArray = React.Children.toArray(children)
  const bodyContent: React.ReactNode[] = []
  let footerContent: React.ReactNode | null = null

  childrenArray.forEach(child => {
    if (React.isValidElement(child) && child.type === Actions.Footer) {
      footerContent = child

      if (React.isValidElement(footerContent)) {
        footerContent = React.cloneElement(footerContent, {
          invertCloseButton: invertCloseButton,
          noCancelButton: !!closeAfter,
          ...footerContent.props,
        })
      }
    } else {
      bodyContent.push(child)
    }
  })

  // If no footer content is provided, render a default footer
  if (!footerContent) {
    footerContent = (
      <Actions.Footer invertCloseButton={invertCloseButton} noCancelButton={!!closeAfter} />
    )
  }

  return (
    <Route path={path}>
      <div
        className={clsx(
          styles.actionsContent,
          context.allowOverflow && styles.allowOverflow,
          className
        )}
      >
        {header && (
          <div className={styles.actionsHeader}>
            <h3>{header}</h3>
          </div>
        )}
        <div className={clsx(styles.actionsBody, context.allowOverflow && styles.allowOverflow)}>
          {bodyContent}
        </div>
        {footerContent}
      </div>
    </Route>
  )
}

const Footer = ({
  children,
  className,
  invertCloseButton,
  noCancelButton = false,
}: {
  children?: React.ReactNode
  className?: string
  invertCloseButton?: boolean
  noCancelButton?: boolean
}) => {
  const context = useModalContext()

  return (
    <div className={clsx(styles.actionsFooter, className)}>
      {children}
      {!noCancelButton && !context.isPendingPage && (
        <Button
          disabled={context.isActionInProgress}
          onClick={e => {
            e.stopPropagation()
            context.toggleModal(false)
          }}
          className={clsx(styles.actionButton, invertCloseButton && stylesButton.buttonSecondary)}
        >
          Close
        </Button>
      )}
    </div>
  )
}

const Link = ({
  to,
  children,
  disabled,
  className,
  icon,
  align,
}: {
  to: string
  children: React.ReactNode
  disabled?: boolean
  className?: string
  icon?: React.ReactNode
  align?: ButtonAlign
}) => {
  const history = useHistory()
  return (
    <Button
      className={clsx(styles.actionButton, className)}
      disabled={disabled}
      onClick={() => history.push(to)}
      icon={icon}
      align={align}
    >
      {children}
    </Button>
  )
}

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

const ActionButton = ({
  action,
  linkPending,
  linkSuccess,
  linkFailure,
  children,
  disabled,
  className,
  icon,
  align,
}: {
  action:
    | ((e?: React.MouseEvent<HTMLButtonElement>) => boolean)
    | ((e?: React.MouseEvent<HTMLButtonElement>) => Promise<boolean>)
  linkPending?: string
  linkSuccess?: string
  linkFailure?: string
  children: React.ReactNode
  disabled?: boolean
  className?: string
  icon?: React.ReactNode
  align?: ButtonAlign
}) => {
  const context = useModalContext()
  const history = useHistory()
  const onClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (linkPending) {
      history.push(linkPending)
      context.setIsPendingPage(true)
    }

    // Define an array of promises to wait on
    const promises: Promise<boolean | void>[] = [Promise.resolve(action(e))]

    // If linkPending, add a 300ms delay to array
    if (linkPending) {
      promises.push(delay(300))
    }
    try {
      // Add a type assertion to the result
      const [actionResult] = (await Promise.all(promises)) as [boolean, ...void[]]

      if (actionResult) {
        if (linkSuccess) {
          context.setIsPendingPage(false)
          history.push(linkSuccess)
          return
        }
        context.toggleModal(false)
      } else {
        if (linkFailure) {
          context.setIsPendingPage(false)
          history.push(linkFailure)
          return
        }
      }
    } catch (e) {
      context.setIsPendingPage(false)
      if (linkFailure) history.push(linkFailure)
      return
    }
  }
  return (
    <Button
      className={clsx(styles.actionButton, className)}
      disabled={disabled}
      onClick={e => onClick(e)}
      icon={icon}
      align={align}
    >
      {children}
    </Button>
  )
}

export const Actions = {
  Modal,
  Page,
  Footer,
  Link,
  Button: ActionButton,
}
